import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';
import {
  Home, GraduationCap, ClipboardCheck, User, LogOut, Menu, X,
  ChevronRight, BarChart3, Building2, Users, Newspaper, Target,
  Trophy, Zap, MessageCircle, Timer, FileText, Sparkles, Bell,
  Wrench, Star, BookOpen, TrendingUp, ShieldCheck, DollarSign, CalendarDays
} from 'lucide-react';
import FloatingRooty from './FloatingRooty';
import UserAvatar from './UserAvatar';
import NewsBar from './NewsBar';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from './ui/sheet';

const BOTTOM_TABS = [
  { path: '/home', label: 'Home', icon: Home },
  { path: '/audits', label: 'Checklists', icon: ClipboardCheck },
  { path: '/actions', label: 'Actions', icon: Zap },
  { path: '/training', label: 'Training', icon: GraduationCap },
  { path: '/profile', label: 'Profile', icon: User },
];

const ALL = ['owner', 'area_manager', 'manager', 'employee', 'supervisor', 'director', 'operator'];
const MGMT = ['owner', 'area_manager', 'director'];
const LEADS = ['owner', 'area_manager', 'manager', 'director'];
const sidebarSections = [
  {
    id: 'ops', label: 'Daily Operations', icon: ClipboardCheck, color: 'text-orange-600', bgActive: 'bg-orange-50', roles: ALL,
    items: [
      { path: '/overview', label: 'Overview', icon: BarChart3, roles: MGMT },
      { path: '/home', label: 'Restaurant Home', icon: Home, roles: ALL },
      { path: '/audits', label: 'Audits & Checklists', icon: ClipboardCheck, roles: ALL },
      { path: '/templates', label: 'Templates', icon: FileText, roles: MGMT },
      { path: '/inbox', label: 'Reports Inbox', icon: Newspaper, roles: MGMT },
      { path: '/actions', label: 'Corrective Actions', icon: Zap, roles: ALL },
      { path: '/compliance', label: 'Compliance', icon: ShieldCheck, roles: ALL },
      { path: '/service-tickets', label: 'Equipment', icon: Wrench, roles: ALL },
    ]
  },
  {
    id: 'performance', label: 'Performance & KPIs', icon: TrendingUp, color: 'text-green-600', bgActive: 'bg-green-50', roles: LEADS,
    items: [
      { path: '/sales', label: 'Sales & KPIs', icon: DollarSign, roles: LEADS },
      { path: '/weekly-dashboard', label: 'Weekly Dashboard', icon: CalendarDays, roles: ALL },
      { path: '/financials', label: 'Financials', icon: FileText, roles: LEADS },
      { path: '/yearly-summary', label: 'Year Review', icon: Trophy, roles: MGMT },
      { path: '/reviews', label: 'Operator Reviews', icon: Star, roles: LEADS },
      { path: '/stores', label: 'Restaurants', icon: Building2, roles: LEADS },
    ]
  },
  {
    id: 'learning', label: 'People & Learning', icon: GraduationCap, color: 'text-indigo-600', bgActive: 'bg-indigo-50', roles: ALL,
    items: [
      { path: '/training', label: 'Training LMS', icon: GraduationCap, roles: ALL },
      { path: '/best-practices', label: 'Best Practices', icon: BookOpen, roles: ALL },
      { path: '/resources', label: 'Resources', icon: ClipboardCheck, roles: ALL },
      { path: '/ai-assistant', label: 'Rooty (AI)', icon: Sparkles, roles: LEADS },
    ]
  },
  {
    id: 'admin', label: 'Admin', icon: Users, color: 'text-slate-700', bgActive: 'bg-slate-100', roles: LEADS,
    items: [
      { path: '/users', label: 'Users', icon: Users, roles: LEADS },
      { path: '/quick-tasks', label: 'Quick Tasks', icon: Zap, roles: LEADS },
      { path: '/messaging', label: 'Messages', icon: MessageCircle, roles: LEADS },
    ]
  }
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['ops', 'performance']);
  const [showProfile, setShowProfile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = ['owner', 'area_manager'].includes(user?.role);

  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`);
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifs && !e.target.closest('[data-testid*="notif"]')) setShowNotifs(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showNotifs]);

  const markAllRead = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    if (notif.link) navigate(notif.link);
    setShowNotifs(false);
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Keep the section that contains the active route expanded
  useEffect(() => {
    const active = sidebarSections.find(s => s.items.some(i => i.path === location.pathname));
    if (active) setExpandedSections(prev => prev.includes(active.id) ? prev : [...prev, active.id]);
  }, [location.pathname]);

  const filteredSections = sidebarSections
    .filter(s => s.roles.includes(user?.role))
    .map(s => ({ ...s, items: s.items.filter(i => i.roles.includes(user?.role)) }))
    .filter(s => s.items.length > 0);

  const isTabActive = (path) => {
    if (path === '/home') return location.pathname === '/home';
    if (path === '/actions') return location.pathname.startsWith('/actions');
    if (path === '/audits') return location.pathname.startsWith('/audit');
    if (path === '/training') return location.pathname.startsWith('/training');
    if (path === '/profile') return false;
    return false;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20 lg:pb-0">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/60">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png" alt="A&W" className="w-8 h-8 object-contain" />
            <span className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="relative">
              <button onClick={() => setShowNotifs(!showNotifs)} className="w-10 h-10 flex items-center justify-center text-slate-600 hover:bg-slate-100 rounded-xl transition-colors relative" data-testid="notification-bell">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 max-h-96 overflow-hidden" data-testid="notif-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <h3 className="font-bold text-sm text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-orange-500 hover:text-orange-600 font-medium">Mark all read</button>
                    )}
                  </div>
                  <div className="overflow-y-auto max-h-72">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">No notifications yet</p>
                    ) : notifications.map(n => (
                      <button key={n.id} onClick={() => handleNotifClick(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-orange-50/50' : ''}`}>
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />}
                          <div className={!n.read ? '' : 'ml-4'}>
                            <p className="text-sm font-medium text-slate-800 leading-snug">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{n.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="w-10 h-10 flex items-center justify-center text-slate-600 hover:bg-slate-100 rounded-xl transition-colors" data-testid="mobile-menu-btn">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button onClick={() => setShowProfile(true)} className="ml-0.5" data-testid="mobile-header-avatar" aria-label="Account and sign out">
              <UserAvatar name={user?.name} picture={user?.picture} size="sm" />
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-full w-[260px] bg-white border-r border-slate-100 transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] lg:translate-x-0 ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="hidden lg:flex items-center justify-between px-5 h-16 border-b border-slate-100/80">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png" alt="A&W" className="w-9 h-9 object-contain" />
              <div>
                <span className="text-[15px] font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood</span>
                <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">Management</p>
              </div>
            </div>
            <div className="relative">
              <button onClick={() => setShowNotifs(!showNotifs)} className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-lg transition-colors relative" data-testid="desktop-notif-bell">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute left-0 top-10 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 max-h-96 overflow-hidden" data-testid="desktop-notif-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <h3 className="font-bold text-sm text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-orange-500 hover:text-orange-600 font-medium">Mark all read</button>
                    )}
                  </div>
                  <div className="overflow-y-auto max-h-72">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">No notifications yet</p>
                    ) : notifications.map(n => (
                      <button key={n.id} onClick={() => handleNotifClick(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-orange-50/50' : ''}`}>
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />}
                          <div className={!n.read ? '' : 'ml-4'}>
                            <p className="text-sm font-medium text-slate-800 leading-snug">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile top padding */}
          <div className="lg:hidden h-14" />

          {/* Nav sections */}
          <nav className="flex-1 overflow-y-auto px-3 py-3">
            {filteredSections.map((section) => {
              const SIcon = section.icon;
              const isExpanded = expandedSections.includes(section.id);
              const hasActive = section.items.some(i => location.pathname === i.path);
              return (
                <div key={section.id} className="mb-0.5">
                  <button onClick={() => toggleSection(section.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-[13px] font-semibold transition-all ${hasActive ? `${section.bgActive} ${section.color}` : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}>
                    <div className="flex items-center gap-2.5"><SIcon className="w-[16px] h-[16px]" />{section.label}</div>
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="mt-0.5 ml-3.5 pl-3 border-l-[1.5px] border-slate-100 space-y-0.5">
                      {section.items.map((item) => {
                        const IIcon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] transition-all ${isActive ? 'bg-orange-500 text-white font-medium shadow-sm shadow-orange-200' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}>
                            <IIcon className="w-[15px] h-[15px]" />{item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center gap-2.5 px-2 mb-2.5">
              <UserAvatar name={user?.name} picture={user?.picture} size="sm" testId="sidebar-user-avatar" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-slate-800 truncate">{user?.name}</p>
                <p className="text-[11px] text-slate-400">{isAdmin ? 'Admin' : 'Team Member'}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 transition-colors" data-testid="sidebar-logout-button">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="lg:hidden fixed inset-0 z-30 bg-black/30 backdrop-blur-[2px]" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="lg:ml-[260px] min-h-screen pt-14 lg:pt-0">
        <NewsBar />
        <div className="p-4 sm:p-5 lg:p-8 max-w-6xl mx-auto animate-fade-in">
          {children}
        </div>
        <footer className="hidden lg:block border-t border-slate-100 py-5 px-6 text-center">
          <p className="text-[11px] text-slate-400">Created by <span className="text-slate-500 font-medium">Mohit Chugh</span> &middot; Elmwood Management</p>
        </footer>
      </main>

      {/* Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-slate-200/60 safe-bottom" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-[60px] max-w-md mx-auto">
          {BOTTOM_TABS.map(tab => {
            const Icon = tab.icon;
            const active = isTabActive(tab.path);
            if (tab.path === '/profile') {
              return (
                <button key={tab.path} onClick={() => setShowProfile(true)} className="flex flex-col items-center justify-center gap-0.5 w-16 py-1 text-slate-400 active:scale-95 transition-transform" data-testid="bottom-nav-profile">
                  <div className="flex items-center justify-center w-8 h-8 rounded-xl"><User className="w-[22px] h-[22px]" /></div>
                  <span className="text-[10px] font-medium leading-tight">Profile</span>
                </button>
              );
            }
            return (
              <Link key={tab.path} to={tab.path} className={`flex flex-col items-center justify-center gap-0.5 w-16 py-1 transition-all active:scale-95 ${active ? 'text-orange-500' : 'text-slate-400'}`} data-testid={`bottom-nav-${tab.label.toLowerCase()}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-xl transition-colors ${active ? 'bg-orange-100' : ''}`}>
                  <Icon className={`w-[22px] h-[22px] ${active ? 'stroke-[2.5px]' : ''}`} />
                </div>
                <span className={`text-[10px] leading-tight ${active ? 'font-semibold' : 'font-medium'}`}>{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Profile drawer (replaces accidental-logout tab) */}
      <Sheet open={showProfile} onOpenChange={setShowProfile}>
        <SheetContent side="bottom" className="rounded-t-3xl pb-8" data-testid="profile-drawer">
          <SheetTitle className="sr-only">Profile menu</SheetTitle>
          <SheetDescription className="sr-only">Your account, quick links and sign out</SheetDescription>
          <div className="max-w-md mx-auto pt-2">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <UserAvatar name={user?.name} picture={user?.picture} size="md" testId="profile-drawer-avatar" />
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-900 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{(user?.role || 'team member').replace('_', ' ')}{user?.store_name ? ` · ${user.store_name}` : ''}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 py-4">
              {[{ to: '/home', label: 'Home', icon: Home }, { to: '/training', label: 'Training', icon: GraduationCap }, { to: '/best-practices', label: 'SOPs', icon: BookOpen }].map(l => {
                const LIcon = l.icon;
                return (
                  <button key={l.to} onClick={() => { setShowProfile(false); navigate(l.to); }} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`profile-link-${l.label.toLowerCase()}`}>
                    <LIcon className="w-5 h-5 text-[#F26522]" />
                    <span className="text-xs font-medium text-slate-700">{l.label}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => { if (window.confirm('Sign out of Elmwood Management?')) handleLogout(); }} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-50 text-red-600 font-semibold text-sm hover:bg-red-100 transition-colors" data-testid="profile-drawer-logout-button">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <FloatingRooty />
    </div>
  );
}
